import { General } from "./general.js";
import { DrawerContent } from "./drawerContent.js";
import { Helper, AfterMountCallbackInvoker } from "../app/core/util/helper.js";
import { SideTabType } from "./sideTabs.js";
import { Config } from "../app/core/config/config.js";
import * as UnloadHelper from "../app/core/util/pageUnloadHandler.js";
import { NotificationHelper } from "../app/core/util/notificationHelper.js";
import { EventType } from "./eventManager.js";
import { Saveable } from "../app/core/components/commonApp.js";
import { BoolReturnFunc, StringKeyDictionary } from "../app/commonInterfaces.js";
import { InnosoftMobileApp } from "../app/core/util/innosoftMobileApp.js";
import React from "react";
import ReactDOM from "react-dom/client";

export class Sheet {
    private _hasValue = Helper.hasValue;
    private _wrapperElement: JQuery = null;
    private _sheetElement: JQuery = null;
    private _titleElement: JQuery = null;
    private _titleLabelElement: JQuery = null;
    private _titleLabelTextElement: JQuery = null;
    private _titleRemarksElement: JQuery = null;
    private _menuElement: JQuery = null;
    private _closeButtonElement: JQuery = null;
    private _contentElement: JQuery = null;
    private _toolbarElement: JQuery = null;
    private _hiddenElement: JQuery = null;
    private _parentElement: JQuery = null;
    private _loadingScreen: JQuery = null;
    private _contextActionsElement: JQuery = null;
    private _mainActionsElement: JQuery = null;
    private _cornerActions: JQuery = null;
    private _remindersElement: JQuery = null;
    private _bookmarkElement: JQuery = null;
    private _sideTabsContentElement: JQuery = null;
    private _leftDrawerContent: DrawerContent = null;
    private _isHidden = true;
    private _awaitsContent = false;
    private _mountTriggered = false;
    private _signal: SheetActionSignaling = (type: string, data?: any) => { }
    private _noOp = () => { };
    private _boolNoOp = () => { return true; };
    private _showTriggered = false;
    private _onAfterAnimation: Array<Function> = [];
    private _animationDelay = 300;//ms
    private _animationData = {
        startTime: null as number,
        endTime: null as number,
        currentTime: null as number,
        factor: 100 / this._animationDelay,
        isRunning: false
    };
    private _noAnimation = false;
    private _triggerClose = true;
    private _customEventCallbacks: StringKeyDictionary<Array<(data?: any) => void>> = {};
    private _sheetInteractionEventCallbacks: StringKeyDictionary<Array<BoolReturnFunc>> = {
        [SheetInteractionEventType.CanRemove]: [],
        [SheetInteractionEventType.CanHide]: [],
        [SheetInteractionEventType.CanShow]: [],
    };
    private _sheetAfterInteractionEventCallbacks: StringKeyDictionary<Array<Function>> = {
        [SheetAfterInteractionEventType.AfterHide]: [],
        [SheetAfterInteractionEventType.AfterRemove]: [],
        [SheetAfterInteractionEventType.AfterShow]: [],
    };
    private _destroyAfterHide = true;
    private _triggerDataCache: StringKeyDictionary<Array<any>> = {};
    private _sourceUrl: string = null;
    private _guid: string = null;
    private _unloadHelper: UnloadHelper.UnloadHelper = null;
    private _afterAnimationCallbacks = {
        remove: null as Function,
        show: null as Function,
        hide: null as Function
    };
    private _removing = false;
    private _hiding = false;
    private _showing = false;
    private _sourceSheet: Sheet = null;
    private _externalData: any = null;
    private _saveTitle: string = null;
    private _externalNewDataCheck: ExternalNewDataCheck = null;


    constructor(config: SheetConfigOptions) {
        this._guid = kendo.guid();

        this._leftDrawerContent = config.leftDrawerContent;
        this._leftDrawerContent.disableAnimation();
        this._leftDrawerContent.hide(null, true);
        this._leftDrawerContent.enableAnimation();

        let build = true;
        if (this._hasValue(config.existingWrapper)) {
            build = false;
        }

        if (this._hasValue(config.awaitsContent)) {
            this._awaitsContent = config.awaitsContent;
        }

        if (this._hasValue(config.signalHandler)) {
            this._signal = config.signalHandler;
        }

        this._noAnimation = config.noAnimation || false || Config.settings.isMobileApp;
        if (this._noAnimation) {
            this._leftDrawerContent.disableAnimation();
        } else {
            this._leftDrawerContent.enableAnimation();
        }

        if (this._hasValue(config.destroyOnHide)) {
            this._destroyAfterHide = config.destroyOnHide;
        }

        if (build) {
            this._wrapperElement = $("<div>").addClass("sheet-wrapper");
            this._sheetElement = $("<div>").addClass("sheet");
            this._contentElement = $("<div>").addClass("sheet__content");
            this._toolbarElement = $("<div>").addClass("sheet__toolbar");
            this._titleElement = $("<div>").addClass("sheet__title");
            this._titleLabelElement = $("<div>").addClass("sheet__title__label");
            this._titleLabelTextElement = $("<div>").addClass("sheet__title__label__text").addClass("truncate");
            this._titleRemarksElement = $("<div>").addClass("sheet__title__label_remarks");
            this._menuElement = $("<div>").addClass("sheet__actions").addClass("sheet__actions--active");

            this._loadingScreen = $("<div>").addClass("sheet__loading-screen");
            this._loadingScreen.html(window["ajaxLoaderImage"]);
            this._sheetElement.append(this._loadingScreen);

            this._titleLabelElement.append(this._titleLabelTextElement);
            this._titleLabelElement.append(this._titleRemarksElement);
            this._titleElement.append(this._titleLabelElement);
            this._sheetElement.append(this._titleElement);
            this._sheetElement.append(this._toolbarElement);
            this._sheetElement.append(this._contentElement);
            this._sheetElement.append(this._menuElement);

            this._wrapperElement.append(this._sheetElement);
        } else {
            this._wrapperElement = config.existingWrapper;
            this._sheetElement = $(".sheet", this._wrapperElement);
            this._toolbarElement = $(".sheet__toolbar", this._wrapperElement);
            this._contentElement = $(".sheet__content", this._wrapperElement);
            this._titleElement = $(".sheet__title", this._wrapperElement);
            this._menuElement = $(".sheet__menu", this._wrapperElement);
            //TODO check if defined and if not, create
            this._parentElement = this._wrapperElement.parent();
        }

        this._remindersElement = $("<div title='" + Config.translations.myTaskReminder + "'>").addClass("sheet__reminders");
        this._titleElement.append(this._remindersElement);

        this._bookmarkElement = $("<div>").addClass("sheet__reminders");
        this._titleElement.append(this._bookmarkElement);

        this._cornerActions = $("<div>").addClass("sheet__corner-actions");

        this._mainActionsElement = $("<div>").addClass("sheet__corner-actions__main-actions");
        this._cornerActions.append(this._mainActionsElement);

        this._closeButtonElement = $("<div title='" + Config.translations.closeSheet + "'>")
            .addClass("sheet__close-button")
            .on("click", () => {
                if (!this.events.onCloseButtonClicked()) {
                    return;
                }

                if (this._wrapperElement.prev(".sheet-wrapper").length > 0) {
                    this.hide();
                } else if (Config.settings.isMobileApp) {
                    InnosoftMobileApp.close();
                }
            });

        if (!Config.settings.isMobileApp) {
            this._closeButtonElement.html("<i class='fa-regular fa-xmark-large fa-xl'></i>");
            this._cornerActions.append(this._closeButtonElement);
        } else {
            this._closeButtonElement.html("<i class='fa-regular fa-chevron-left fa-xl'></i>");
            this._titleElement.prepend(this._closeButtonElement);

            InnosoftMobileApp.showCloseButton().then(showCloseButton => {
                if (showCloseButton) {
                    this._closeButtonElement.addClass("sheet__close-button-show");
                }
            });
        }

        this._contextActionsElement = $("<div>").addClass("sheet__corner-actions__context-actions");
        this._cornerActions.append(this._contextActionsElement);

        this._titleElement.append(this._cornerActions);

        this._sideTabsContentElement = General.Drawer.SideTabs.addTab({
            name: kendo.guid(),
            onSelect: () => { },
            sheet: this,
            type: SideTabType.Sheet
        });
    }


    private _moveSheet = (toHiddenContainer: boolean) => {
        if (toHiddenContainer) {
            if (this._hasValue(this._hiddenElement)) {
                this._wrapperElement.appendTo(this._hiddenElement);
                this._isHidden = true;
            }

        } else {
            if (this._hasValue(this._parentElement)) {
                this._wrapperElement.appendTo(this._parentElement);
                this._isHidden = false;
            }
        }
    }

    private _animate = (fromBottom: boolean) => {
        this._resetAnimationData();
        const fnc = (time) => {
            this._animationData.isRunning = true;
            //const reqId = requestAnimationFrame(fnc);

            if (this._animationData.startTime === null) {
                this._animationData.startTime = Math.floor(time);
                this._animationData.endTime = this._animationData.startTime + this._animationDelay;
            }
            this._animationData.currentTime = Math.floor(time);

            let percentage = Math.min((this._animationData.currentTime - this._animationData.startTime) * this._animationData.factor, 100);
            if (fromBottom) {
                percentage = 100 - percentage;
            }

            this._wrapperElement.css({ transform: "translateY(" + percentage + "%)" });

            const cancel = fromBottom ? percentage <= 0 : percentage >= 100;
            //console.log(this._animationData);
            //console.log(time);
            //console.log(percentage);
            //console.log("-------------------");
            if (cancel) {
                //cancelAnimationFrame(reqId);
                this._animationData.isRunning = false;
                let after: Function = null;
                while (after = this._onAfterAnimation.pop()) {
                    after();
                }
                this._resetAnimationData();
            } else {
                requestAnimationFrame(fnc);
            }
        }
        requestAnimationFrame(fnc);
    }

    private _resetAnimationData = () => {
        this._animationData.currentTime = null;
        this._animationData.endTime = null;
        this._animationData.factor = 100 / this._animationDelay;
        this._animationData.startTime = null;
    }

    private _cleaUp = () => {
        this.unmountContent();
        this._wrapperElement.remove();
        this._leftDrawerContent.remove();
        UnloadHelper.removeHanlder(this._unloadHelper);
    }

    public CleanUp() {
        this._cleaUp();
    }

    private _runBoolReturnFuncArray = (source: Array<BoolReturnFunc>) => {
        for (let i = 0; i < source.length; i++) {
            const result = source[i]();
            if (result === false) {
                return false;
            }
        }

        return true;
    }

    private _runFuncArray = (source: Array<Function>) => {
        source.forEach(f => f());
    }

    //private _setDrawerContentWidth = (dc: DrawerContent, width: string | number) => {
    //    dc.setWidth(width);
    //}

    private _processUrl = (url: string) => {
        let tmp = url;

        if (this._hasValue(tmp)) {
            if (tmp.indexOf(Config.baseUrl) === 0) {
                tmp = tmp.substr(Config.baseUrl.length);
            }
            tmp = tmp.replace(/&isWindow=true/gi, "");
            tmp = tmp.replace(/isWindow=true/gi, "");
        }

        return tmp;
    }


    setParentElement = (parent: JQuery) => {
        this._parentElement = parent;
        if (!this._isHidden) {
            this._moveSheet(false);
        }
    }

    setHiddenWrapperElement = (element: JQuery) => {
        this._hiddenElement = element;
        if (this._isHidden) {
            this._moveSheet(true);
        }
    }

    awaitsComponentInitialization = (status?: boolean) => {
        if (this._hasValue(status)) {
            this._awaitsContent = status;
        }
        this._signal("reserved", this._awaitsContent);
        return this._awaitsContent;
    }

    remove = (onAnimationEnd?: Function) => {
        //this._removing = true;
        const after = () => {
            setTimeout(() => {
                if (this._unloadHelper) {
                    this._unloadHelper.runAllCallbacks();
                }

                this._cleaUp();

                if (onAnimationEnd) {
                    onAnimationEnd();
                }
                this._afterAnimationCallbacks.remove = null;

                this._runFuncArray(this._sheetAfterInteractionEventCallbacks[SheetAfterInteractionEventType.AfterRemove]);

                General.Events.trigger(EventType.SheetRemoved);
                this._signal("remove", this);
            }, 50);
        };

        const removeIt = () => {
            if (this._runBoolReturnFuncArray(this._sheetInteractionEventCallbacks[SheetInteractionEventType.CanRemove])) {
                const reallyRemoveIt = () => {
                    //if (this._sourceSheet) {
                    //    this._sourceSheet.unbindAfterInteraction(SheetAfterInteractionEventType.AfterRemove, reallyRemoveIt);
                    //}

                    if (this._animationData.isRunning) {
                        if (this._isHidden) {
                            this._onAfterAnimation.push(after);
                        } else {
                            this.hide(after, true);
                        }
                    } else {
                        if (this._isHidden) {
                            after();

                        } else {
                            this.hide(after, true);
                        }
                    }
                }

                //if (this._sourceSheet) {
                //    this._sourceSheet.bindAfterInteraction(SheetAfterInteractionEventType.AfterRemove, reallyRemoveIt);
                //} else {
                reallyRemoveIt();
                //}
            } else {
                this._afterAnimationCallbacks.show = onAnimationEnd;
            }
        }

        if (!this._removing) {
            if (this._sourceSheet) {
                this._sourceSheet.bindAfterInteraction(SheetAfterInteractionEventType.AfterRemove, removeIt);

                if (!this.isHidden) {
                    //...
                    this.hide(onAnimationEnd, true);
                } else {
                    removeIt();
                }
            } else {
                this._removing = true;
                const showUnloadMessage = !this._hiding && this._unloadHelper && !this._unloadHelper.isEntitySaved();

                if (showUnloadMessage) {
                    this._unloadHelper.showUnloadMessage().then(discard => {
                        if (discard) {
                            removeIt();
                        } else {
                            this._removing = false;
                        }
                    });
                } else {
                    removeIt();
                }
            }
        }
    }

    show = (onAnimationEnd?: Function) => {
        this._showing = true;
        this._hiding = false;
        if (this._isHidden && this._runBoolReturnFuncArray(this._sheetInteractionEventCallbacks[SheetInteractionEventType.CanShow])) {
            this.events.trigger(SheetEventType.Open);

            const after = () => {
                this._leftDrawerContent.show(() => {
                    if (onAnimationEnd) {
                        onAnimationEnd();
                    }
                    this._afterAnimationCallbacks.show = null;

                    if (!this._showTriggered) {
                        this._showTriggered = true;
                        if (this._hasValue(this._loadingScreen)) {
                            this._loadingScreen.remove();
                        }
                    }

                    this._runFuncArray(this._sheetAfterInteractionEventCallbacks[SheetAfterInteractionEventType.AfterShow]);
                    General.Events.trigger(EventType.SheetOpened);
                    this._showing = false;
                    this._signal("show", true);
                });
            };

            if (this._noAnimation) {
                this._moveSheet(false);
                after();
            } else {
                this._onAfterAnimation.push(after);

                this._wrapperElement.css({ tranform: "translateY(100%)" });
                this._moveSheet(false);

                this._animate(true);
            }
        } else {
            this._afterAnimationCallbacks.show = () => {
                if (onAnimationEnd) {
                    onAnimationEnd();
                }
                this._showing = false;
            }
        }
    }

    hide = (onAnimationEnd?: Function, preventRemove?: boolean) => {
        this._hiding = true;
        const hideIt = async () => {
            if (!this._isHidden && this._runBoolReturnFuncArray(this._sheetInteractionEventCallbacks[SheetInteractionEventType.CanHide])) {
                if (this._triggerClose) {
                    this.events.trigger(SheetEventType.Close);
                }
                const after = () => {
                    this._leftDrawerContent.hide(() => {
                        this._moveSheet(true);

                        if (onAnimationEnd) {
                            onAnimationEnd();
                        }
                        this._afterAnimationCallbacks.hide = null;

                        //popState -> see historyStateManager.tsx
                        if (this._afterCloseCallback) {
                            this._afterCloseCallback();
                        }

                        this._runFuncArray(this._sheetAfterInteractionEventCallbacks[SheetAfterInteractionEventType.AfterHide]);
                        General.Events.trigger(EventType.SheetClosed);

                        if (this._destroyAfterHide && preventRemove !== true) {
                            this.remove();
                        }

                        this._signal("show", false);
                    }, true);
                };

                if (this._noAnimation) {
                    after();
                } else {
                    this._onAfterAnimation.push(after);
                    this._animate(false);
                }
            } else {
                this._afterAnimationCallbacks.hide = onAnimationEnd;
            }
        }

        const showUnloadMessage = !this._removing && this._unloadHelper && this._unloadHelper.isEnabled() && !this._unloadHelper.isEntitySaved();

        if (showUnloadMessage) {
            this._unloadHelper.showUnloadMessage().then(discard => {
                if (discard) {
                    hideIt();
                } else {
                    this._hiding = false;
                }
            });
        } else {
            hideIt();
        }
    }

    isHidden = () => {
        return this._isHidden;
    }

    isRemoving = () => {
        return this._removing;
    }

    isHiding = () => {
        return this._hiding;
    }

    isShowing = () => {
        return this._showing;
    }
    private sheetRoot;

    mountContent = <T extends React.ComponentClass<P>, P>(component: T, props: P, afterMount?: Function) => {
        const created = React.createElement(component, props);
        if (!this.sheetRoot) {
            this.sheetRoot = ReactDOM["createRoot"](this._contentElement[0]);
        }
        this.sheetRoot.render(<AfterMountCallbackInvoker onAfterMount={afterMount}>{created}</AfterMountCallbackInvoker>);
        this._mountTriggered = true;
        this._signal("mount");
    }

    unmountContent = () => {
        if (this._mountTriggered) {
            this.sheetRoot.unmount();
        }
        this._signal("unmount");
    }

    appendContent = (content: JQuery) => {
        this._contentElement.append(content);
        this._signal("content", content);
    }

    appendStringContent = (content: string) => {
        this._contentElement.html(content);
        this._signal("content", content);
    }

    clearContent = () => {
        this._contentElement.empty();
        this._signal("content", null);
    }

    setTitle = (title: string) => {
        //TODO content? string? text()?
        this._saveTitle = title;
        if (!Helper.isNullOrEmpty(title)) {
            const label: any = $("<label>").attr("title", title).text(title);
            this._titleLabelTextElement.html(label);
        }
    }

    getTitle = (): string => {
        return this._saveTitle;
    }

    elements = {
        getContentRef: (): JQuery => {
            return this._contentElement;
        },
        getElement: (): JQuery => {
            return this._wrapperElement;
        },
        getTitleRef: (): JQuery => {
            return this._titleLabelTextElement;
        },
        getTitleRemarksRef: (): JQuery => {
            return this._titleRemarksElement;
        },
        getLeftDrawerElementRef: (): JQuery => {
            return this._leftDrawerContent.getElement();
        },
        getRightDrawerElementRef: (): JQuery => {
            return this._sideTabsContentElement;
        },
        getMenuRef: (): JQuery => {
            return this._menuElement;
        },
        getMainActionsRef: (): JQuery => {
            return this._mainActionsElement;
        },
        getContextActionsRef: (): JQuery => {
            return this._contextActionsElement;
        },
        getRemindersRef: (): JQuery => {
            return this._remindersElement;
        },
        getBookmarkRef: (): JQuery => {
            return this._bookmarkElement;
        },
        getToolbarRef: (): JQuery => {
            return this._toolbarElement;
        }
    }

    events: SheetEvents = {
        onCloseButtonClicked: this._boolNoOp,
        bind: (evName, callback) => {
            if (!this._customEventCallbacks[evName]) {
                this._customEventCallbacks[evName] = [];
            }

            this._customEventCallbacks[evName].push(callback);

            //the event was already triggered
            if (this._hasValue(this._triggerDataCache[evName])) {
                //for AppMounted, call the callback immediately
                if (evName === SheetEventType.AppMounted) {
                    callback(this._triggerDataCache[evName][0]);
                }
            }
        },
        unbind: (evName, callback) => {
            if (this._hasValue(callback)) {
                if (Array.isArray(this._customEventCallbacks[evName])) {
                    const idx = this._customEventCallbacks[evName].indexOf(callback);
                    if (idx >= 0) {
                        this._customEventCallbacks[evName].splice(idx, 1);
                    }
                }
            } else {
                this._customEventCallbacks[evName].length = 0;
            }
        },
        trigger: (evName, data) => {
            if (Array.isArray(this._customEventCallbacks[evName])) {
                //clear the triggered cache
                delete this._triggerDataCache[evName];
                //call all the callbacks
                this._customEventCallbacks[evName].forEach(c => c(data));
            } else {
                //no callback registered
                //special case: it can be that the event is triggered befor the callback is registered
                //save the data for later
                if (!Array.isArray(this._triggerDataCache[evName])) {
                    this._triggerDataCache[evName] = [];
                }
                this._triggerDataCache[evName].push(data);
            }
        },
        bindInteraction: (evName, callback) => {
            this._sheetInteractionEventCallbacks[evName].push(callback);
        },
        unbindInteraction: (evName, callback) => {
            if (this._hasValue(callback)) {
                const idx = this._sheetInteractionEventCallbacks[evName].indexOf(callback);
                if (idx >= 0) {
                    this._sheetInteractionEventCallbacks[evName].splice(idx, 1);
                }
            } else {
                this._sheetInteractionEventCallbacks[evName].length = 0;
            }
        },
        bindAfterInteraction: (evName, callback) => {
            this._sheetAfterInteractionEventCallbacks[evName].push(callback);
        },
        unbindAfterInteraction: (evName, callback) => {
            if (this._hasValue(callback)) {
                const idx = this._sheetAfterInteractionEventCallbacks[evName].indexOf(callback);
                if (idx >= 0) {
                    this._sheetAfterInteractionEventCallbacks[evName].splice(idx, 1);
                }
            } else {
                this._sheetAfterInteractionEventCallbacks[evName].length = 0;
            }
        }
    }

    bind = (evName: SheetEventType, callback: (data?: any) => void) => {
        this.events.bind(evName, callback);
    }

    trigger = (evName: SheetEventType, data?: any) => {
        this.events.trigger(evName, data);
    }

    unbind = (evName: SheetEventType, callback?: (data?: any) => void) => {
        this.events.unbind(evName, callback);
    }

    bindInteraction = (evName: SheetInteractionEventType, callback: BoolReturnFunc) => {
        this.events.bindInteraction(evName, callback);
    }

    unbindInteraction = (evName: SheetInteractionEventType, callback: BoolReturnFunc) => {
        this.events.unbindInteraction(evName, callback);
    }

    bindAfterInteraction = (evName: SheetAfterInteractionEventType, callback: Function) => {
        this.events.bindAfterInteraction(evName, callback);
    }

    unbindAfterInteraction = (evName: SheetAfterInteractionEventType, callback: Function) => {
        this.events.unbindAfterInteraction(evName, callback);
    }

    destroyOnHide = (destroy: boolean, source: Sheet) => {
        if (destroy !== true) {
            if (!Helper.hasValue(source)) {
                NotificationHelper.showErrorMessage("Please specify source sheet!");
                throw Error("Please specify source sheet!");
            } else {
                this._sourceSheet = source;
            }
        } else {
            this._sourceSheet = null;
        }
        this._destroyAfterHide = destroy;
    }
    openLeftDrawer = () => {
        if (General.Drawer.Left.isOpen()) {
            this._leftDrawerContent.show();
        } else {
            General.Drawer.Left.open(() => {
                this._leftDrawerContent.show();
            });
        }
    }

    unmountLeftDrawerContentOnRemove = (on: boolean) => {
        this._leftDrawerContent.unmountOnRemove(on);
    }

    setSourceUrl = (url: string) => {
        this._sourceUrl = this._processUrl(url);
    }

    getSourceUrl = () => {
        return this._sourceUrl;
    }

    id = () => {
        return this._guid;
    }


    private _afterCloseCallback: Function = null;
    /* ================================================
     *| ACHTUNG: Soll nur aus StateManager aufgerufen! |
     * ================================================
     * */
    setUpAfterCloseForPopState = (callback: Function) => {
        this._afterCloseCallback = Helper.hasValue(callback) ? callback : null;
    }

    setUpUnloadHelper = (loadedModules: Saveable[] = null) => {
        if (!this._unloadHelper) {
            this._unloadHelper = UnloadHelper.createSheetHandler(loadedModules);
        }

        return this._unloadHelper;
    }

    hasUnsavedChanges = () => {
        return this._unloadHelper && !this._unloadHelper.isEntitySaved();
    }

    getUnloadHelper = () => {
        return this._unloadHelper;
    }

    prepareForCascadeRemove = () => {
        if (this._sourceSheet) {
            return false;
        }
        return true;
    }

    getExternalData = () => {
        return this._externalData;
    }

    setExternalData = (data: any) => {
        this._externalData = data;
    }

    getExternalNewDataCheck = () => {
        // console.log("getExternalNewDataCheck", this.id());
        return this._externalNewDataCheck
    }

    setExternalNewDataCheck = (data: ExternalNewDataCheck) => {
        // console.log("setExternalNewDataCheck", this.id());
        this._externalNewDataCheck = data;
    }

    setNoAnimation = (noAnimation: boolean) => {
        this._noAnimation = noAnimation;
    }

    setTriggerClose = (triggerClsoe: boolean) => {
        this._triggerClose = triggerClsoe;
    }
}

export interface SheetConfigOptions {
    leftDrawerContent: DrawerContent;
    awaitsContent?: boolean;
    signalHandler?: SheetActionSignaling;
    existingWrapper?: JQuery;
    noAnimation?: boolean;
    destroyOnHide?: boolean;
}

export interface ExternalNewDataCheck {
    check: (object: any) => boolean;
}

export interface SheetActionSignaling {
    (action: "show", data: boolean): void;
    (action: "mount", data: null): void;
    (action: "unmount", data: null): void;
    (action: "content", data: string | JQuery): void;
    (action: "remove", sheet: Sheet): void;
    (action: "reserved", data: boolean): void;
    (action: string, data?: any): void;
}

interface SheetEvents {
    onCloseButtonClicked: BoolReturnFunc;
    bind: (evType: SheetEventType, callback: (data?: any) => void) => void;
    unbind: (evType: SheetEventType, callback: (data?: any) => void) => void;
    trigger: (evType: SheetEventType, data?: any) => void;
    bindInteraction: (evType: SheetInteractionEventType, callback: BoolReturnFunc) => void;
    unbindInteraction: (evType: SheetInteractionEventType, callback: BoolReturnFunc) => void;
    bindAfterInteraction: (evType: SheetAfterInteractionEventType, callback: Function) => void;
    unbindAfterInteraction: (evType: SheetAfterInteractionEventType, callback: Function) => void;
}

export enum SheetEventType {
    Close, Open, RowClicked, ItemsSelected, SetSelectedItems, SavedSuccessfully, ItemDeleted, AppMounting, AppMounted, Custom
}

export enum SheetInteractionEventType {
    CanShow = "canshow", CanHide = "canhide", CanRemove = "canremove"
}

export enum SheetAfterInteractionEventType {
    AfterShow = "aftershow", AfterHide = "afterhide", AfterRemove = "afterremove"
}